import { useEffect, useState } from 'react';
import { Button, Card, Popconfirm, Table, Tooltip, Typography } from 'antd';
import moment from 'moment';
import { useHistory } from 'react-router-dom';

import callApi from '../../Api/config';
import { AppRoutes } from '../../constants/AppConstant';
import {
  DOCUMITRA_ORDER_APPLICATION,
  ORDER_USER,
  PHOTO_ORDER_APPLICATION,
} from '../../constants/EntityName';
import { toastError, toastSuccess } from '../../service/Toast';

import './style.scss';

const { Text } = Typography;
const DEFAULT_PAGE_SIZE = 10;

interface UserOrderHistoryProps {
  userId: string;
}

const getResponseData = (response: any): any => {
  return response?.data?.data ?? response?.data ?? {};
};

const getResults = (response: any): any[] => {
  const data = response?.data?.data ?? response?.data;
  return Array.isArray(data) ? data : data?.results || data?.items || [];
};

const getTotalCount = (response: any): number => {
  const data = getResponseData(response);
  const totalCount = Number(data?.totalCount);
  return Number.isFinite(totalCount) ? totalCount : getResults(response).length;
};

/** Calendar-safe date for order/payment dates (matches Documitra Order display). */
const formatOrderDate = (value?: string | Date | null) => {
  if (!value) return '--';
  const raw = String(value).trim();
  if (!raw || raw === '--') return '--';

  // Date-only (YYYY-MM-DD) — parse as local calendar day to avoid UTC off-by-one
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly && (raw.length === 10 || raw[10] === 'T' || raw[10] === ' ')) {
    const parsed = moment(`${dateOnly[1]}-${dateOnly[2]}-${dateOnly[3]}`, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '--';
  }

  const parsed = moment(raw);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '--';
};

const getPaymentStatus = (order: any) =>
  order?.paymentDetails?.paymentStatus || order?.paymentStatus || '--';

// Settled one way or the other — either the money is in, or it came back out.
const SETTLED_PAYMENT_STATUSES = [
  'payment successful',
  'payment done',
  'paid',
  'completed',
  'refund issued',
  'refunded',
];

// The reminder asks the customer to finish their form and payment, so it only
// makes sense while the order is still unpaid.
const isPaymentOutstanding = (order: any) => {
  const status = String(getPaymentStatus(order)).trim().toLowerCase();
  return !SETTLED_PAYMENT_STATUSES.includes(status);
};

const getOrderAmount = (order: any) => {
  const amount =
    order?.paymentDetails?.amountPaid ??
    order?.totalDiscountedAmount ??
    order?.totalAmount ??
    order?.amount;
  if (amount === null || amount === undefined || amount === '') return '--';
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return String(amount);
  const currency = String(
    order?.currency || order?.paymentDetails?.currency || 'USD'
  ).toUpperCase();
  const symbol = currency === 'INR' ? '₹' : '$';
  return `${symbol}${numeric.toFixed(2)}`;
};

const getServiceName = (order: any) =>
  order?.serviceName || order?.service?.name || order?.package?.service?.name || '--';

const getPackageName = (order: any) => order?.packageName || order?.package?.name || '--';

const getPlanName = (order: any) =>
  order?.pricingPlan?.title || order?.plan || order?.pricingPlan?.name || '--';

/** Same source as Documitra Order → Payment Details (paymentDetails.paymentDate). */
const getPaymentDate = (order: any) => {
  const paymentDate = order?.paymentDetails?.paymentDate || order?.paymentDate || null;

  // Do not fall back to updatedAt/createdAt — those are not the payment date.
  return formatOrderDate(paymentDate);
};

const getApplicationStatus = (order: any) => {
  const memberStatuses = (order?.members || [])
    .map((member: any) => member?.status)
    .filter(Boolean);
  const candidate =
    order?.applicationStatus ||
    order?.orderStatus ||
    order?.memberStatus ||
    memberStatuses.join(', ') ||
    order?.status;
  const normalized = String(candidate || '')
    .trim()
    .toLowerCase();
  const paymentStatus = String(getPaymentStatus(order)).trim().toLowerCase();

  // The listing APIs may mirror a payment value into `status`. Do not present
  // that value as the application's progress; it belongs in Payment Status.
  if (!candidate || normalized === paymentStatus || /^(payment|paid)/.test(normalized)) {
    return '--';
  }

  return candidate;
};

const getPaymentStatusStyle = (label: string) => {
  const norm = String(label || '')
    .trim()
    .toLowerCase();
  let style = { bg: '#f1f5f9', color: '#475569', ring: 'rgba(71,85,105,0.2)' };
  if (norm === 'payment pending') {
    style = { bg: '#fffbeb', color: '#b45309', ring: 'rgba(180,83,9,0.2)' };
  } else if (norm === 'payment successful' || norm === 'payment done' || norm === 'paid') {
    style = { bg: '#ecfdf5', color: '#047857', ring: 'rgba(5,150,105,0.2)' };
  } else if (norm === 'refund issued' || norm === 'refunded') {
    style = { bg: '#fdf2f8', color: '#9d174d', ring: 'rgba(157,23,77,0.2)' };
  } else if (norm === 'payment failed' || norm === 'failed') {
    style = { bg: '#fef2f2', color: '#b91c1c', ring: 'rgba(185,28,28,0.2)' };
  } else if (norm === 'partially paid' || norm === 'partial payment') {
    style = { bg: '#eff6ff', color: '#1d4ed8', ring: 'rgba(29,78,216,0.2)' };
  }
  return style;
};

const renderPaymentStatusBadge = (order: any) => {
  const label = getPaymentStatus(order);
  if (!label || label === '--') {
    return <span style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>—</span>;
  }
  const style = getPaymentStatusStyle(String(label));
  return (
    <span
      style={{
        display: 'inline-flex',
        width: 'fit-content',
        alignItems: 'center',
        borderRadius: '999px',
        padding: '2px 10px',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'default',
        background: style.bg,
        color: style.color,
        boxShadow: `inset 0 0 0 1px ${style.ring}`,
      }}
    >
      {label}
    </span>
  );
};

const renderPlanWithImage = (order: any) => {
  const planTitle =
    order?.pricingPlan?.title || order?.plan || order?.pricingPlan?.name || getPlanName(order);

  if (!planTitle || planTitle === '--') return '--';

  const normalized = String(planTitle).trim();

  if (normalized === 'Online Consultation') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Tooltip title="Online Consultation">
          <img
            src="/onlineconsultationicon.png"
            alt="Online Consultation"
            style={{ width: '25px', height: '25px' }}
          />
        </Tooltip>
      </div>
    );
  }
  if (normalized === 'Ready to Ship') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Tooltip title="Ready to Ship">
          <img
            src="/readytoshipicon.png"
            alt="Ready to Ship"
            style={{ width: '35px', height: '25px' }}
          />
        </Tooltip>
      </div>
    );
  }
  return '--';
};

const UserOrderHistory = ({ userId }: UserOrderHistoryProps) => {
  const history = useHistory();
  const [documentLoading, setDocumentLoading] = useState(true);
  const [photoLoading, setPhotoLoading] = useState(true);
  const [documentOrders, setDocumentOrders] = useState<any[]>([]);
  const [photoOrders, setPhotoOrders] = useState<any[]>([]);
  const [documentPagination, setDocumentPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [photoPagination, setPhotoPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [reminderLoading, setReminderLoading] = useState<{ [key: string]: boolean }>({});

  const sendReminder = async (order: any) => {
    const rowKey = String(order?.orderId || order?.id);
    // Prefer the display id: it is what the admin sees in this table.
    const targetOrderId = order?.orderId || order?.id;

    setReminderLoading(previous => ({ ...previous, [rowKey]: true }));
    try {
      const response = await callApi(`${ORDER_USER}/${userId}/send-reminder`, 'POST', null, {
        orderId: targetOrderId,
      });
      const payload = response?.data;
      if (response?.status && (payload?.isSuccess === 1 || payload?.statusCode === 200)) {
        toastSuccess(payload?.message || 'Reminder sent');
      } else {
        toastError(payload?.message || 'Could not send reminder');
      }
    } catch {
      toastError('Could not send reminder');
    } finally {
      setReminderLoading(previous => ({ ...previous, [rowKey]: false }));
    }
  };

  useEffect(() => {
    let mounted = true;

    const fetchDocumentOrders = async () => {
      setDocumentLoading(true);
      try {
        const documentResponse = await callApi(DOCUMITRA_ORDER_APPLICATION, 'GET', null, {
          page: documentPagination.current,
          limit: documentPagination.pageSize,
          userId,
        });
        if (!mounted) return;
        setDocumentOrders(documentResponse?.status ? getResults(documentResponse) : []);
        setDocumentPagination(previous => ({
          ...previous,
          total: documentResponse?.status ? getTotalCount(documentResponse) : 0,
        }));
      } catch {
        if (mounted) {
          setDocumentOrders([]);
          setDocumentPagination(previous => ({ ...previous, total: 0 }));
        }
      } finally {
        if (mounted) setDocumentLoading(false);
      }
    };

    if (userId) fetchDocumentOrders();

    return () => {
      mounted = false;
    };
  }, [userId, documentPagination.current, documentPagination.pageSize]);

  useEffect(() => {
    let mounted = true;

    const fetchPhotoOrders = async () => {
      setPhotoLoading(true);
      try {
        const photoResponse = await callApi(PHOTO_ORDER_APPLICATION, 'GET', null, {
          page: photoPagination.current,
          limit: photoPagination.pageSize,
          userId,
        });
        if (!mounted) return;
        setPhotoOrders(photoResponse?.status ? getResults(photoResponse) : []);
        setPhotoPagination(previous => ({
          ...previous,
          total: photoResponse?.status ? getTotalCount(photoResponse) : 0,
        }));
      } catch {
        if (mounted) {
          setPhotoOrders([]);
          setPhotoPagination(previous => ({ ...previous, total: 0 }));
        }
      } finally {
        if (mounted) setPhotoLoading(false);
      }
    };

    if (userId) fetchPhotoOrders();

    return () => {
      mounted = false;
    };
  }, [userId, photoPagination.current, photoPagination.pageSize]);

  const columns = [
    {
      title: 'Order ID',
      key: 'orderId',
      render: (_: unknown, order: any) => order?.orderId || order?.id || '--',
    },
    {
      title: 'Application Status',
      key: 'applicationStatus',
      render: (_: unknown, order: any) => getApplicationStatus(order),
    },
    {
      title: 'Plan',
      key: 'plan',
      render: (_: unknown, order: any) => renderPlanWithImage(order),
    },
    {
      title: 'Payment Status',
      key: 'paymentStatus',
      render: (_: unknown, order: any) => renderPaymentStatusBadge(order),
    },
    {
      title: 'Total Amount',
      key: 'amount',
      render: (_: unknown, order: any) => getOrderAmount(order),
    },
    {
      title: 'Order Date',
      key: 'orderDate',
      render: (_: unknown, order: any) => formatOrderDate(order?.createdAt),
    },
    {
      title: 'Payment Date',
      key: 'paymentDate',
      render: (_: unknown, order: any) => getPaymentDate(order),
    },
  ];

  const renderStackedMembers = (order: any) => {
    const members = Array.isArray(order?.members) ? order.members : [];
    if (!members.length) return '--';

    return (
      <div style={{ maxWidth: '200px' }}>
        {members.map((member: any, index: number) => (
          <div
            key={member?.id || `${order?.id || order?.orderId}-${index}`}
            style={{
              marginBottom: index < members.length - 1 ? '4px' : '0',
              padding: '3px 6px',
              borderRadius: '3px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#333',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {member?.name || member?.fullName || '--'}
          </div>
        ))}
      </div>
    );
  };

  const renderStackedMemberStatuses = (order: any) => {
    const members = Array.isArray(order?.members) ? order.members : [];
    if (!members.length) {
      return getApplicationStatus(order);
    }

    return (
      <div style={{ maxWidth: '250px' }}>
        {members.map((member: any, index: number) => {
          const status =
            member?.status || (order?.status ? order.status : 'Form Filling Under Process');

          return (
            <div
              key={member?.id || `${order?.id || order?.orderId}-status-${index}`}
              style={{
                marginBottom: index < members.length - 1 ? '4px' : '0',
                padding: '3px 6px',
                borderRadius: '3px',
                fontSize: '14px',
                color: '#1890ff',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {status}
            </div>
          );
        })}
      </div>
    );
  };

  const documentOrderRows = documentOrders.map(order => ({
    ...order,
    key: String(order?.id || order?.orderId || Math.random()),
  }));

  const documentColumns = [
    {
      title: 'Order ID',
      key: 'orderId',
      render: (_: unknown, row: any) => {
        const orderId = row?.orderId || row?.id;
        if (!orderId) return '--';

        return (
          <a
            style={{ color: '#323232', textDecoration: 'underline' }}
            href={`${AppRoutes.DocumitraOrderApplication}?search=${encodeURIComponent(orderId)}`}
            onClick={event => {
              event.preventDefault();
              history.push(
                `${AppRoutes.DocumitraOrderApplication}?search=${encodeURIComponent(orderId)}`
              );
            }}
          >
            {orderId}
          </a>
        );
      },
    },
    {
      title: 'Service Name',
      key: 'serviceName',
      render: (_: unknown, row: any) => getServiceName(row),
    },
    {
      title: 'Package Name',
      key: 'packageName',
      render: (_: unknown, row: any) => getPackageName(row),
    },
    {
      title: 'Member Name',
      key: 'memberName',
      render: (_: unknown, row: any) => renderStackedMembers(row),
    },
    {
      title: 'Application Status',
      key: 'applicationStatus',
      render: (_: unknown, row: any) => renderStackedMemberStatuses(row),
    },
    {
      title: 'Plan',
      key: 'plan',
      render: (_: unknown, row: any) => renderPlanWithImage(row),
    },
    {
      title: 'Payment Status',
      key: 'paymentStatus',
      render: (_: unknown, row: any) => {
        const rowKey = String(row?.orderId || row?.id);
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '6px',
            }}
          >
            {renderPaymentStatusBadge(row)}
            {isPaymentOutstanding(row) && (
              <Popconfirm
                title="Send a Send Follow-Up to complete the form and payment?"
                okText="Send Email"
                cancelText="Cancel"
                onConfirm={() => sendReminder(row)}
                disabled={!!reminderLoading[rowKey]}
              >
                <Button type="primary" size="small" loading={!!reminderLoading[rowKey]}>
                  Send Follow-Up
                </Button>
              </Popconfirm>
            )}
          </div>
        );
      },
    },
    {
      title: 'Total Amount',
      key: 'amount',
      render: (_: unknown, row: any) => getOrderAmount(row),
    },
    {
      title: 'Order Date',
      key: 'orderDate',
      render: (_: unknown, row: any) => formatOrderDate(row?.createdAt),
    },
    {
      title: 'Payment Date',
      key: 'paymentDate',
      render: (_: unknown, row: any) => getPaymentDate(row),
    },
  ];

  return (
    <section className="user-order-history" aria-label="User order history">
      <div className="user-order-history__grid">
        <Card title={`Documitra Order History (${documentPagination.total})`} size="small">
          <Table
            rowKey="key"
            columns={documentColumns}
            dataSource={documentOrderRows}
            bordered
            loading={documentLoading}
            pagination={{
              current: documentPagination.current,
              pageSize: documentPagination.pageSize,
              total: documentPagination.total,
              showSizeChanger: true,
              showTotal: total => `Total ${total} orders`,
              onChange: (current, pageSize) =>
                setDocumentPagination(previous => ({
                  ...previous,
                  current,
                  pageSize,
                })),
            }}
            locale={{ emptyText: 'No Documitra orders found for this user.' }}
            scroll={{ x: 1400 }}
            size="small"
          />
        </Card>
        <Card title={`Photo Order History (${photoPagination.total})`} size="small">
          <Table
            rowKey={order => order?.id || order?.orderId}
            columns={columns}
            dataSource={photoOrders}
            loading={photoLoading}
            bordered
            pagination={{
              current: photoPagination.current,
              pageSize: photoPagination.pageSize,
              total: photoPagination.total,
              showSizeChanger: true,
              showTotal: total => `Total ${total} orders`,
              onChange: (current, pageSize) =>
                setPhotoPagination(previous => ({
                  ...previous,
                  current,
                  pageSize,
                })),
            }}
            locale={{ emptyText: 'No photo orders found for this user.' }}
            scroll={{ x: 1000 }}
            size="small"
          />
        </Card>
      </div>
    </section>
  );
};

export default UserOrderHistory;
